import { defineStore } from 'pinia'
import { 
  login, 
  loginWithPhone, 
  loginWithEmail, 
  sendVerificationCode,
  logout, 
  getCurrentUser, 
  refreshToken 
} from '@/services/userService'

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    isLoggedIn: false,
    isLoading: false,
    error: null,
    token: null,
    refreshToken: null,
    // 验证码相关状态
    verificationCode: {
      loading: false,
      countdown: 0,
      phone: '',
      email: ''
    },
    // 微信登录状态
    wechatLogin: {
      loading: false,
      qrCodeUrl: '',
      sessionKey: '',
      status: 'pending'
    }
  }),
  
  getters: {
    username: (state) => state.user?.nickname || state.user?.username || '',
    hasError: (state) => !!state.error,
    userAvatar: (state) => state.user?.avatar || '',
    userRoles: (state) => state.user?.roles || []
  },
  
  actions: {
    // 用户名密码登录
    async loginUser(username, password, remember = false) {
      this.isLoading = true
      this.error = null
      
      try {
        const response = await login(username, password, remember)
        if (response.code === 0 && response.data) {
          this.setUserData(response.data, remember)
          return this.user
        } else {
          throw new Error(response.message || '登录失败')
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || '登录失败，请重试'
        // 将错误重新抛出，让组件能够捕获
        throw error
      } finally {
        this.isLoading = false
      }
    },

    // 发送验证码
    async sendCode(type, target, purpose = 'login') {
      this.verificationCode.loading = true
      
      try {
        const response = await sendVerificationCode(type, target, purpose)
        if (response.code === 0) {
          if (type === 'phone') {
            this.verificationCode.phone = target
          } else if (type === 'email') {
            this.verificationCode.email = target
          }
          this.startCountdown()
          return response
        } else {
          throw new Error(response.message || '发送验证码失败')
        }
      } finally {
        this.verificationCode.loading = false
      }
    },

    // 手机验证码登录
    async loginWithPhoneCode(phone, code, remember = false) {
      this.isLoading = true
      this.error = null
      
      try {
        const response = await loginWithPhone(phone, code, remember)
        if (response.code === 0 && response.data) {
          this.setUserData(response.data, remember)
          return this.user
        } else {
          throw new Error(response.message || '登录失败')
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || '登录失败，请重试'
        // 将错误重新抛出，让组件能够捕获
        throw error
      } finally {
        this.isLoading = false
      }
    },

    // 邮箱验证码登录
    async loginWithEmailCode(email, code, remember = false) {
      this.isLoading = true
      this.error = null
      
      try {
        const response = await loginWithEmail(email, code, remember)
        if (response.code === 0 && response.data) {
          this.setUserData(response.data, remember)
          return this.user
        } else {
          throw new Error(response.message || '登录失败')
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || '登录失败，请重试'
        // 将错误重新抛出，让组件能够捕获
        throw error
      } finally {
        this.isLoading = false
      }
    },

    // 设置用户数据
    setUserData(userData, remember = false) {
      this.user = {
        userId: userData.userId,
        username: userData.username,
        avatar: userData.avatar,
        roles: userData.roles,
        email: userData.email,
        phone: userData.phone,
        nickname: userData.nickname,
        memberLevel: userData.memberLevel,
        memberExpiry: userData.memberExpiry,
        balance: userData.balance,
        giftBalance: userData.giftBalance
      }
      this.token = userData.token
      this.refreshToken = userData.refreshToken
      this.isLoggedIn = true
      
      // 存储用户信息
      if (remember) {
        localStorage.setItem('user', JSON.stringify(this.user))
        localStorage.setItem('token', this.token)
        localStorage.setItem('refreshToken', this.refreshToken)
      } else {
        sessionStorage.setItem('user', JSON.stringify(this.user))
        sessionStorage.setItem('token', this.token)
        sessionStorage.setItem('refreshToken', this.refreshToken)
      }
      
      // 清理微信登录状态
      this.wechatLogin = {
        loading: false,
        qrCodeUrl: '',
        sessionKey: '',
        status: 'pending'
      }
    },

    // 验证码倒计时
    startCountdown(duration = 60) {
      this.verificationCode.countdown = duration
      const timer = setInterval(() => {
        this.verificationCode.countdown--
        if (this.verificationCode.countdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    },
    
    async checkAuth() {
      // 尝试从localStorage或sessionStorage恢复用户会话
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user')
      const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token')
      const storedRefreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken')
      
      if (storedUser && storedToken) {
        try {
          this.user = JSON.parse(storedUser)
          this.token = storedToken
          this.refreshToken = storedRefreshToken
          this.isLoggedIn = true
          
          // 验证存储的用户信息是否有效
          const response = await getCurrentUser()
          if (response.code === 0 && response.data) {
            // 更新用户信息
            this.user = response.data
            // 更新存储
            if (localStorage.getItem('user')) {
              localStorage.setItem('user', JSON.stringify(this.user))
            } else {
              sessionStorage.setItem('user', JSON.stringify(this.user))
            }
          } else {
            // 如果服务器返回无效会话，尝试刷新token
            await this.refreshUserToken()
          }
        } catch (error) {
          // 如果刷新token失败，则登出
          this.logoutUser()
        }
      }
    },

    async refreshUserToken() {
      if (!this.refreshToken) {
        throw new Error('无刷新令牌')
      }
      
      const response = await refreshToken(this.refreshToken)
      if (response.code === 0 && response.data) {
        this.token = response.data.token
        this.refreshToken = response.data.refreshToken
        
        // 更新存储
        if (localStorage.getItem('token')) {
          localStorage.setItem('token', this.token)
          localStorage.setItem('refreshToken', this.refreshToken)
        } else {
          sessionStorage.setItem('token', this.token)
          sessionStorage.setItem('refreshToken', this.refreshToken)
        }
        
        return true
      } else {
        throw new Error(response.message || '刷新token失败')
      }
    },
    
    async logoutUser() {
      this.isLoading = true
      
      try {
        const response = await logout()
        if (response.code !== 0) {
          console.warn('服务器登出异常，仍将清除本地状态', response.message)
        }
      } catch (error) {
        console.error('登出错误，仍将清除本地状态', error)
      } finally {
        this.user = null
        this.token = null
        this.refreshToken = null
        this.isLoggedIn = false
        this.isLoading = false
        
        // 重置验证码状态
        this.verificationCode = {
          loading: false,
          countdown: 0,
          phone: '',
          email: ''
        }
        
        // 重置微信登录状态
        this.wechatLogin = {
          loading: false,
          qrCodeUrl: '',
          sessionKey: '',
          status: 'pending'
        }
        
        // 清除用户身份凭证存储
        localStorage.removeItem('user')
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        sessionStorage.removeItem('user')
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('refreshToken')
        
        // 清除搜索相关的本地缓存
        localStorage.removeItem('caseSearchState')
        localStorage.removeItem('caseDetail')
        localStorage.removeItem('legalSearchState')
        localStorage.removeItem('legalDetail')
        localStorage.removeItem('caseSearchResults')
        localStorage.removeItem('searchResults')
      }
    },
    
    clearError() {
      this.error = null
    }
  }
}) 